import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import swal from "sweetalert";
import ReactQuill from "react-quill";
import {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiCode,
  FiEdit3,
  FiPlus,
  FiTrash2,
  FiCopy,
  FiSmartphone,
  FiMonitor,
  FiRotateCcw,
  FiRotateCw,
  FiTag,
  FiCheck,
  FiLayers,
  FiImage,
  FiType,
  FiChevronUp,
  FiChevronDown,
  FiX,
  FiSliders,
  FiCheckCircle,
  FiStar,
} from "react-icons/fi";

import Layout from "../../components/global/layout";
import loader from "../../methods/loader";
import FormControl from "../../components/common/FormControl";
import {
  MODULE_CONFIG,
  STANDARD_EMAIL_TYPES,
  EMAIL_TYPE_MAP,
  DEFAULT_BUILDER_CONFIG,
  generateEmailHtml,
  renderWithVariables,
  EmailTemplateService,
} from "./shared";

// Standard fonts for emails
const EMAIL_FONTS = [
  "Arial",
  "Helvetica",
  "Inter",
  "Roboto",
  "Georgia",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
];

// Standard font sizes
const FONT_SIZES = [
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "22px",
  "24px",
  "28px",
  "32px",
];

// =====================================================================
// 1. DYNAMIC MERGE VARIABLES ORGANIZED STRICTLY BY EMAIL TYPE
// =====================================================================
const VARIABLES_BY_TYPE = {
  ORDER_STATUS_UPDATE: [
    { key: "order_id", description: "Order Reference ID", example: "ORD-1092", recommended: true },
    { key: "order_status", description: "Current Order Status", example: "Dispatched", recommended: true },
    { key: "order_date", description: "Order Date", example: "Oct 19, 2026", recommended: true },
    { key: "tracking_number", description: "Shipping Tracking Number", example: "TRK-990182", recommended: true },
    { key: "action_url", description: "Track Order Status Link", example: "https://portal.charlessmithassociates.com/orders/ORD-1092", recommended: true },
    { key: "customer_name", description: "Customer Full Name", example: "Bob Johnson" },
    { key: "customer_email", description: "Customer Email Address", example: "bob.johnson@example.com" },
    { key: "platform_name", description: "Company / Platform Name", example: "Charles S. Smith & Associates" },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png" },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com" },
    { key: "platform_phone", description: "Company Phone Number", example: "+1 (555) 019-2834" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],

  QUOTE_NOTIFICATION: [
    { key: "quote_number", description: "Quote Reference Number", example: "QT-7890", recommended: true },
    { key: "quote_amount", description: "Total Estimated Quote Amount", example: "$4,500.00", recommended: true },
    { key: "quote_expiry_date", description: "Quote Expiration Date", example: "Nov 18, 2026", recommended: true },
    { key: "quote_items_count", description: "Total Number of Quoted Items", example: "3 items", recommended: true },
    { key: "action_url", description: "Approve / Review Quote Link", example: "https://portal.charlessmithassociates.com/quotes/QT-7890", recommended: true },
    { key: "customer_name", description: "Client Full Name", example: "Bob Johnson" },
    { key: "customer_email", description: "Client Email Address", example: "bob.johnson@example.com" },
    { key: "platform_name", description: "Company / Platform Name", example: "Charles S. Smith & Associates" },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png" },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com" },
    { key: "platform_phone", description: "Company Phone Number", example: "+1 (555) 019-2834" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],

  INQUIRY_REPLY: [
    { key: "inquiry_id", description: "Inquiry Reference ID", example: "INQ-5521", recommended: true },
    { key: "inquiry_subject", description: "Inquiry Subject Line", example: "Commercial Plumbing Inquiry", recommended: true },
    { key: "inquiry_message", description: "Customer Inquiry Text", example: "We need an estimate for 3 commercial sites.", recommended: true },
    { key: "staff_responder_name", description: "Staff Responder Name", example: "David Miller", recommended: true },
    { key: "action_url", description: "View Inquiry Thread Link", example: "https://portal.charlessmithassociates.com/inquiries/5521", recommended: true },
    { key: "customer_name", description: "Client Full Name", example: "Jane Miller" },
    { key: "customer_email", description: "Client Email Address", example: "jane.miller@example.com" },
    { key: "platform_name", description: "Company Name", example: "Charles S. Smith & Associates" },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png" },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com" },
    { key: "platform_phone", description: "Company Phone Number", example: "+1 (555) 019-2834" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],

  TENANT_WELCOME: [
    { key: "tenant_name", description: "Organization / Tenant Name", example: "Acme Enterprises", recommended: true },
    { key: "tenant_domain", description: "Tenant Subdomain URL", example: "acme.jcdesk.cloud", recommended: true },
    { key: "login_email", description: "Admin Setup Login Email", example: "admin@acme.com", recommended: true },
    { key: "action_url", description: "Activate Account & Sign In Link", example: "https://portal.charlessmithassociates.com/setup", recommended: true },
    { key: "customer_name", description: "Administrator Name", example: "Sarah Connor" },
    { key: "platform_name", description: "Company Name", example: "Charles S. Smith & Associates" },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png" },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com" },
    { key: "platform_phone", description: "Company Phone Number", example: "+1 (555) 019-2834" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],

  SUBSCRIPTION_RECEIPT: [
    { key: "invoice_id", description: "Invoice / Receipt Number", example: "INV-2026-901", recommended: true },
    { key: "plan_name", description: "Subscribed Plan Name", example: "Enterprise Unlimited", recommended: true },
    { key: "amount_paid", description: "Total Amount Paid", example: "$199.00", recommended: true },
    { key: "billing_period", description: "Billing Cycle", example: "Monthly (Oct 2026 - Nov 2026)", recommended: true },
    { key: "payment_method", description: "Payment Method Card", example: "Visa ending in 4242", recommended: true },
    { key: "action_url", description: "Download Invoice PDF Link", example: "https://portal.charlessmithassociates.com/invoices/901", recommended: true },
    { key: "customer_name", description: "Client Full Name", example: "Bob Johnson" },
    { key: "customer_email", description: "Client Email Address", example: "bob.johnson@example.com" },
    { key: "platform_name", description: "Company Name", example: "Charles S. Smith & Associates" },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png" },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],

  CUSTOM: [
    { key: "customer_name", description: "Recipient Full Name", example: "Bob Johnson", recommended: true },
    { key: "customer_email", description: "Recipient Email Address", example: "bob.johnson@example.com", recommended: true },
    { key: "action_url", description: "Primary Button Link", example: "https://portal.charlessmithassociates.com", recommended: true },
    { key: "platform_name", description: "Company Name", example: "Charles S. Smith & Associates", recommended: true },
    { key: "platform_logo", description: "Company Logo URL", example: "/assets/img/logo.png", recommended: true },
    { key: "platform_email", description: "Company Support Email", example: "support@charlessmithassociates.com", recommended: true },
    { key: "platform_phone", description: "Company Phone Number", example: "+1 (555) 019-2834" },
    { key: "current_year", description: "Current Year", example: "2026" },
  ],
};

// =====================================================================
// 2. PREDEFINED STARTER TEMPLATES BY EMAIL TYPE
// =====================================================================
const PREDEFINED_TEMPLATES_BY_TYPE = {
  ORDER_STATUS_UPDATE: {
    name: "Order Status Update Notification",
    subject: "Update on Order #{{order_id}} - {{order_status}}",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Official System Notification",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "left",
          layout: "side-by-side",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "20px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Order Status Update: #{{order_id}}",
          level: "h2",
          color: "#0f172a",
          fontSize: "22px",
          fontFamily: "Arial",
          align: "center",
          isBold: true,
          isItalic: false,
          padding_top: 22,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>Dear <strong>{{customer_name}}</strong>,</p><p>We are pleased to inform you that your order <strong>#{{order_id}}</strong> has been updated to <strong>{{order_status}}</strong>.</p><p>You can track the live status and shipping updates via your portal dashboard at any time.</p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "left",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "Track Order Progress →",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "center",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },

  QUOTE_NOTIFICATION: {
    name: "Quote Estimate Notification",
    subject: "Estimate for {{customer_name}} - {{quote_number}}",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Official Estimate Proposal",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "left",
          layout: "side-by-side",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "20px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Estimate for {{quote_number}}",
          level: "h2",
          color: "#0f172a",
          fontSize: "22px",
          fontFamily: "Arial",
          align: "center",
          isBold: true,
          isItalic: false,
          padding_top: 22,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>Hi <strong>{{customer_name}}</strong>,</p><p>Please find attached the estimate for the works we discussed. If you are happy with everything please click on Approve button. If you have any questions or want to ask something, please click on Ask Question button.</p><p style='margin-top: 12px; font-size: 16px;'>Estimated Total: <strong>{{quote_amount}}</strong></p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "left",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "Accept Estimate",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "center",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },

  INQUIRY_REPLY: {
    name: "Customer Inquiry Reply Notification",
    subject: "Response to Your Inquiry: {{inquiry_subject}}",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Customer Support Team",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "left",
          layout: "side-by-side",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "20px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Regarding Your Inquiry: {{inquiry_subject}}",
          level: "h2",
          color: "#0f172a",
          fontSize: "20px",
          fontFamily: "Arial",
          align: "left",
          isBold: true,
          isItalic: false,
          padding_top: 20,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>Dear <strong>{{customer_name}}</strong>,</p><p>Thank you for reaching out to us. A member of our team has reviewed your inquiry and replied with the requested information.</p><p>If you have any further questions or follow-up notes, please feel free to reply directly or access the client portal.</p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "left",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "View Inquiry Conversation →",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "left",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },

  TENANT_WELCOME: {
    name: "Tenant Welcome Notification",
    subject: "Welcome to {{platform_name}} - Account Configured",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Welcome to the Family",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "center",
          layout: "centered",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "22px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Welcome, {{customer_name}}!",
          level: "h2",
          color: "#0f172a",
          fontSize: "24px",
          fontFamily: "Arial",
          align: "center",
          isBold: true,
          isItalic: false,
          padding_top: 24,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>We are delighted to welcome you to <strong>{{platform_name}}</strong>. Your organization workspace has been created and initialized.</p><p>You can now sign in to your administrative dashboard to start managing operations, staff permissions, and workflows.</p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "center",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "Access Your Console →",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "center",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },

  SUBSCRIPTION_RECEIPT: {
    name: "Subscription Payment Receipt",
    subject: "Receipt for {{plan_name}} - {{amount_paid}}",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Official Billing Receipt",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "left",
          layout: "side-by-side",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "20px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Payment Confirmed: {{amount_paid}}",
          level: "h2",
          color: "#0f172a",
          fontSize: "22px",
          fontFamily: "Arial",
          align: "center",
          isBold: true,
          isItalic: false,
          padding_top: 22,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>Dear <strong>{{customer_name}}</strong>,</p><p>Thank you for your payment. Your subscription for <strong>{{plan_name}}</strong> has been renewed successfully.</p><p style='margin-top: 10px;'>Invoice Reference: <strong>{{invoice_id}}</strong><br/>Billing Period: <strong>{{billing_period}}</strong></p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "left",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "Download Invoice PDF →",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "center",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },

  CUSTOM: {
    name: "Custom Notification Template",
    subject: "Important System Notification from {{platform_name}}",
    blocks: [
      {
        id: "blk-header-1",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "System Notification",
          subtitle_color: "#bfdbfe",
          padding_top: 24,
          padding_bottom: 24,
          padding_left: 28,
          padding_right: 28,
          logo_show: true,
          logo_var: "{{platform_logo}}",
          logo_height: "38",
          logo_align: "left",
          layout: "side-by-side",
          name_show: true,
          name_var: "{{platform_name}}",
          name_font_size: "20px",
          name_color: "#ffffff",
          fontFamily: "Arial",
          isBold: true,
          isItalic: false,
        },
      },
      {
        id: "blk-heading-2",
        type: "heading",
        data: {
          text: "Notification Title",
          level: "h2",
          color: "#0f172a",
          fontSize: "22px",
          fontFamily: "Arial",
          align: "left",
          isBold: true,
          isItalic: false,
          padding_top: 20,
          padding_bottom: 12,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-text-3",
        type: "text",
        data: {
          html: "<p>Dear <strong>{{customer_name}}</strong>,</p><p>This is a custom operational notice regarding your account.</p>",
          color: "#334155",
          fontSize: "14px",
          fontFamily: "Arial",
          align: "left",
          isBold: false,
          isItalic: false,
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-button-4",
        type: "button",
        data: {
          label: "Learn More →",
          url: "{{action_url}}",
          bg_color: "#115B9D",
          text_color: "#ffffff",
          border_radius: "8",
          align: "center",
          fontSize: "14px",
          fontFamily: "Arial",
          isBold: true,
          padding_top: 16,
          padding_bottom: 16,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-divider-5",
        type: "divider",
        data: {
          color: "#e2e8f0",
          thickness: "1",
          padding_top: 14,
          padding_bottom: 14,
          padding_left: 24,
          padding_right: 24,
        },
      },
      {
        id: "blk-footer-6",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
          disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
          fontSize: "12px",
          fontFamily: "Arial",
          align: "center",
          padding_top: 20,
          padding_bottom: 20,
          padding_left: 24,
          padding_right: 24,
        },
      },
    ],
    classicContent: "",
  },
};

export default function EmailTemplateAddEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get("clone");

  const user = useSelector((state) => state.user);
  const isEdit = Boolean(id);

  // Basic Information (Top Form) - CLEAN BLANK INITIAL STATE IN CREATE MODE
  const [name, setName] = useState("");
  const [emailType, setEmailType] = useState("");
  const [subject, setSubject] = useState("");
  const [isDefaultBackup, setIsDefaultBackup] = useState(false);
  const [saving, setSaving] = useState(false);

  // Body Content Tabs: "visual" | "classic" | "code" | "preview"
  const [activeBodyTab, setActiveBodyTab] = useState("visual");

  // Visual Builder Blocks & Selection - CLEAN BLANK INITIAL CANVAS IN CREATE MODE
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [canvasDevice, setCanvasDevice] = useState("desktop"); // "desktop" | "mobile"
  const [rightTab, setRightTab] = useState("add"); // "add" | "style" | "insert"

  // Undo / Redo History Stack
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Global Style Configuration for Canvas
  const [builderConfig, setBuilderConfig] = useState(DEFAULT_BUILDER_CONFIG);

  // Classic Editor Content
  const [classicContent, setClassicContent] = useState("");

  // Raw HTML mode state
  const [rawHtml, setRawHtml] = useState("");
  const [isHtmlModeDirty, setIsHtmlModeDirty] = useState(false);

  // Insert Variable Search Query
  const [varSearchQuery, setVarSearchQuery] = useState("");

  // Test Variables for Live Preview Simulation: Only platform info with fallback, no static customer dummy data
  const [previewTabDevice, setPreviewTabDevice] = useState("desktop");
  const [testVariables, setTestVariables] = useState({
    platform_name: user?.company_name || user?.name || "Charles S. Smith & Associates",
    platform_logo: user?.company_logo || user?.logo || "/assets/img/logo.png",
    platform_email: user?.company_email || user?.email || "support@charlessmithassociates.com",
    platform_phone: user?.company_phone || user?.phone || "N/A",
    current_year: String(new Date().getFullYear()),
  });

  // Sync rawHtml whenever blocks change
  useEffect(() => {
    if (!isHtmlModeDirty) {
      const compiled = generateEmailHtml({
        ...builderConfig,
        blocks,
      });
      setRawHtml(compiled);
    }
  }, [blocks, builderConfig, isHtmlModeDirty]);

  // Push new state to history for undo/redo
  const pushHistory = (newBlocks) => {
    const updatedHistory = history.slice(0, historyIndex + 1);
    updatedHistory.push(newBlocks);
    setHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setBlocks(history[prevIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setBlocks(history[nextIndex]);
    }
  };

  // Load Existing Template (Edit or Clone Mode)
  useEffect(() => {
    const targetId = id || cloneId;
    if (!targetId) return;

    const loadTemplate = async () => {
      loader(true);
      try {
        const res = await EmailTemplateService.detail(targetId);
        const data = res?.data || res?.template;

        if (data) {
          setName(cloneId ? `${data.name || "Template"} (Copy)` : data.name || "");
          setEmailType(data.email_type || "");
          setSubject(data.subject || "");
          setIsDefaultBackup(cloneId ? false : Boolean(data.is_default_backup));

          if (data.builder_config?.blocks && Array.isArray(data.builder_config.blocks)) {
            setBlocks(data.builder_config.blocks);
            setHistory([data.builder_config.blocks]);
            setHistoryIndex(0);
            if (data.builder_config.blocks.length > 0) {
              setSelectedBlockId(data.builder_config.blocks[0].id);
              setRightTab("style");
            }
          }

          if (data.builder_config) {
            setBuilderConfig(data.builder_config);
          }

          if (data.body_html) {
            setRawHtml(data.body_html);
            const extracted = data.body_html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
            setClassicContent(extracted ? extracted[1] : data.body_html);
          }
        } else {
          toast.error("Unable to load email template details.");
        }
      } catch (err) {
        console.error("Failed to load template detail:", err);
        toast.error("Failed to load template.");
      } finally {
        loader(false);
      }
    };

    loadTemplate();
  }, [id, cloneId]);

  // =====================================================================
  // TYPE SELECTION WITH PROMPT: "DO YOU WANT PREDEFINED EMAIL OR NOT?"
  // =====================================================================
  const handleTypeSelect = async (newType) => {
    if (!newType) {
      setEmailType("");
      return;
    }

    const typeConfig = STANDARD_EMAIL_TYPES.find((t) => t.id === newType);
    const typeLabel = typeConfig?.name || newType;

    // Show prompt modal asking if user wants predefined email
    const willLoadPredefined = await swal({
      title: "Load Predefined Template?",
      text: `Do you want to load the predefined starter email and layout for "${typeLabel}"?`,
      icon: "info",
      buttons: {
        cancel: {
          text: "No, Start Blank",
          value: false,
          visible: true,
          className: "bg-slate-200 text-slate-800 font-bold",
          closeModal: true,
        },
        confirm: {
          text: "Yes, Load Predefined Email",
          value: true,
          visible: true,
          className: "bg-[#115B9D] text-white font-bold",
          closeModal: true,
        },
      },
    });

    setEmailType(newType);

    if (willLoadPredefined) {
      const starter = PREDEFINED_TEMPLATES_BY_TYPE[newType] || PREDEFINED_TEMPLATES_BY_TYPE.CUSTOM;
      if (starter) {
        setName(starter.name);
        setSubject(starter.subject);
        setBlocks(starter.blocks);
        pushHistory(starter.blocks);
        setSelectedBlockId(starter.blocks[0]?.id || null);
        setRightTab("style");
        // Body content for the WYSIWYG editor
        const bodyBlocks = starter.blocks.filter((b) => b.type === "text" || b.type === "heading");
        const bodyHtml = bodyBlocks
          .map((b) =>
            b.type === "heading"
              ? `<h${b.data?.level === "h1" ? "1" : "2"}>${b.data?.text || ""}</h${b.data?.level === "h1" ? "1" : "2"}>`
              : b.data?.html || ""
          )
          .join("");
        setClassicContent(starter.classicContent || bodyHtml);
        toast.success(`Loaded predefined template for ${typeLabel}!`);
      }
    } else {
      // User chose to start blank: keep canvas clean, default to Add tab
      setRightTab("add");
      toast.info(`Type set to ${typeLabel}. You can start building from scratch.`);
    }
  };

  // Block Manipulation Helpers
  const addBlock = (type) => {
    let newBlock = null;
    const blockId = `blk-${type}-${Date.now()}`;

    switch (type) {
      case "header":
        newBlock = {
          id: blockId,
          type: "header",
          data: {
            bg_color: "#115B9D",
            text_color: "#ffffff",
            subtitle: "Official System Notification",
            subtitle_color: "#bfdbfe",
            padding_top: 24,
            padding_bottom: 24,
            padding_left: 28,
            padding_right: 28,
            logo_show: true,
            logo_var: "{{platform_logo}}",
            logo_height: "38",
            logo_align: "left",
            layout: "side-by-side",
            name_show: true,
            name_var: "{{platform_name}}",
            name_font_size: "20px",
            name_color: "#ffffff",
            fontFamily: "Arial",
            isBold: true,
            isItalic: false,
          },
        };
        break;
      case "heading":
        newBlock = {
          id: blockId,
          type: "heading",
          data: {
            text: "Important Update",
            level: "h2",
            color: "#0f172a",
            fontSize: "22px",
            fontFamily: "Arial",
            align: "center",
            isBold: true,
            isItalic: false,
            padding_top: 20,
            padding_bottom: 12,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      case "text":
        newBlock = {
          id: blockId,
          type: "text",
          data: {
            html: "<p>Hi <strong>{{customer_name}}</strong>,</p><p>Please find attached the latest notification for the works we discussed. If you have any questions, please click the button below.</p>",
            color: "#334155",
            fontSize: "14px",
            fontFamily: "Arial",
            align: "left",
            isBold: false,
            isItalic: false,
            padding_top: 14,
            padding_bottom: 14,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      case "button":
        newBlock = {
          id: blockId,
          type: "button",
          data: {
            label: "Take Action →",
            url: "{{action_url}}",
            bg_color: "#115B9D",
            text_color: "#ffffff",
            border_radius: "8",
            align: "center",
            fontSize: "14px",
            fontFamily: "Arial",
            isBold: true,
            padding_top: 16,
            padding_bottom: 16,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      case "image":
        newBlock = {
          id: blockId,
          type: "image",
          data: {
            url: "{{platform_logo}}",
            alt: "Artwork",
            align: "center",
            width: "220",
            link_url: "",
            padding_top: 16,
            padding_bottom: 16,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      case "divider":
        newBlock = {
          id: blockId,
          type: "divider",
          data: {
            color: "#e2e8f0",
            thickness: "1",
            padding_top: 12,
            padding_bottom: 12,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      case "footer":
        newBlock = {
          id: blockId,
          type: "footer",
          data: {
            bg_color: "#0f172a",
            text_color: "#cbd5e1",
            email_var: "{{platform_email}}",
            phone_var: "{{platform_phone}}",
            copyright_text: "{{platform_name}}. All rights reserved.",
            disclaimer: "For support, contact us at {{platform_email}} | {{platform_phone}}",
            fontSize: "12px",
            fontFamily: "Arial",
            align: "center",
            padding_top: 20,
            padding_bottom: 20,
            padding_left: 24,
            padding_right: 24,
          },
        };
        break;
      default:
        return;
    }

    const updated = [...blocks, newBlock];
    setBlocks(updated);
    pushHistory(updated);
    setSelectedBlockId(newBlock.id);
    setRightTab("style");
    toast.info(`Added ${type.toUpperCase()} block.`);
  };

  const removeBlock = (blockId, e) => {
    e?.stopPropagation();
    const updated = blocks.filter((b) => b.id !== blockId);
    setBlocks(updated);
    pushHistory(updated);
    if (selectedBlockId === blockId) {
      setSelectedBlockId(updated[0]?.id || null);
      if (!updated.length) {
        setRightTab("add");
      }
    }
  };

  const duplicateBlock = (blockId, e) => {
    e?.stopPropagation();
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx === -1) return;
    const target = blocks[idx];
    const clone = {
      ...target,
      id: `blk-${target.type}-${Date.now()}`,
      data: JSON.parse(JSON.stringify(target.data)),
    };
    const updated = [...blocks];
    updated.splice(idx + 1, 0, clone);
    setBlocks(updated);
    pushHistory(updated);
    setSelectedBlockId(clone.id);
    setRightTab("style");
    toast.success("Block duplicated successfully.");
  };

  const moveBlock = (index, direction, e) => {
    e?.stopPropagation();
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= blocks.length) return;
    const updated = [...blocks];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    setBlocks(updated);
    pushHistory(updated);
  };

  const updateBlockData = (blockId, field, value) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          data: {
            ...b.data,
            [field]: value,
          },
        };
      })
    );
  };

  const handleSelectBlock = (blockId) => {
    setSelectedBlockId(blockId);
    setRightTab("style");
  };

  // Insert Variable helper
  const handleInsertVariable = (varKey) => {
    const token = `{{${varKey}}}`;
    if (selectedBlockId) {
      const block = blocks.find((b) => b.id === selectedBlockId);
      if (block) {
        if (block.type === "heading") {
          updateBlockData(selectedBlockId, "text", `${block.data.text || ""} ${token}`);
          toast.success(`Inserted ${token} into Title`);
          return;
        }
        if (block.type === "text") {
          updateBlockData(selectedBlockId, "html", `${block.data.html || ""} ${token}`);
          toast.success(`Inserted ${token} into Paragraph`);
          return;
        }
        if (block.type === "button") {
          updateBlockData(selectedBlockId, "url", token);
          toast.success(`Set Button URL to ${token}`);
          return;
        }
        if (block.type === "header") {
          updateBlockData(selectedBlockId, "name_var", token);
          toast.success(`Set Header Name to ${token}`);
          return;
        }
        if (block.type === "footer") {
          updateBlockData(selectedBlockId, "disclaimer", `${block.data.disclaimer || ""} ${token}`);
          toast.success(`Inserted ${token} into Footer`);
          return;
        }
      }
    }

    navigator.clipboard?.writeText(token);
    toast.success(`Copied ${token} to clipboard!`);
  };

  // Save / Submit Handler
  const handleSave = async (e) => {
    e?.preventDefault();

    if (!name.trim()) {
      toast.error("Please provide a Title / Name for the template.");
      return;
    }

    if (!emailType) {
      toast.error("Please select an Email Type.");
      return;
    }

    if (!subject.trim()) {
      toast.error("Please provide a Subject line.");
      return;
    }

    // Determine final body_html
    let finalHtml = rawHtml;
    if (activeBodyTab === "visual" || activeBodyTab === "preview") {
      finalHtml = generateEmailHtml({
        ...builderConfig,
        blocks,
      });
    } else if (activeBodyTab === "classic") {
      const headerBlk = blocks.find((b) => b.type === "header") || {
        id: "blk-header",
        type: "header",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          subtitle: "Official System Notification",
          subtitle_color: "#bfdbfe",
          name_show: true,
          name_var: "{{platform_name}}",
        },
      };
      const buttonBlk = blocks.find((b) => b.type === "button");
      const footerBlk = blocks.find((b) => b.type === "footer") || {
        id: "blk-footer",
        type: "footer",
        data: {
          bg_color: "#115B9D",
          text_color: "#ffffff",
          email_var: "{{platform_email}}",
          phone_var: "{{platform_phone}}",
          copyright_text: "{{platform_name}}. All rights reserved.",
        },
      };

      const blocksToSave = [headerBlk];
      blocksToSave.push({
        id: "blk-classic-body",
        type: "text",
        data: {
          html: classicContent,
          color: "#334155",
          fontSize: "15px",
        },
      });
      if (buttonBlk) blocksToSave.push(buttonBlk);
      blocksToSave.push(footerBlk);

      finalHtml = generateEmailHtml({
        ...builderConfig,
        blocks: blocksToSave,
      });
    }

    // Get variables for the selected email type
    const activeVariables = VARIABLES_BY_TYPE[emailType] || VARIABLES_BY_TYPE.CUSTOM;

    const payload = {
      name: name.trim(),
      email_type: emailType,
      subject: subject.trim(),
      body_html: finalHtml,
      body_text: finalHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim(),
      is_default_backup: isDefaultBackup,
      variables: activeVariables,
      builder_config: {
        ...builderConfig,
        blocks,
      },
    };

    loader(true);
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await EmailTemplateService.update(id, payload);
      } else {
        res = await EmailTemplateService.create(payload);
      }

      if (res?.success) {
        toast.success(
          isEdit ? "Email template updated successfully." : "Email template created successfully."
        );
        navigate(MODULE_CONFIG.routeBase);
      } else {
        toast.error(res?.message || "Failed to save email template.");
      }
    } catch (err) {
      console.error("Save template error:", err);
      toast.error("An error occurred while saving the email template.");
    } finally {
      loader(false);
      setSaving(false);
    }
  };

  // Email Type dropdown options
  const typeOptions = STANDARD_EMAIL_TYPES.map((t) => ({
    id: t.id,
    name: t.name,
  }));

  // =====================================================================
  // DYNAMIC MERGE VARIABLES FILTERED BASED ON SELECTED EMAIL TYPE
  // =====================================================================
  const currentTypeVariables = emailType
    ? VARIABLES_BY_TYPE[emailType] || VARIABLES_BY_TYPE.CUSTOM
    : VARIABLES_BY_TYPE.CUSTOM;

  const filteredVariables = currentTypeVariables.filter((v) => {
    if (!varSearchQuery) return true;
    const q = varSearchQuery.toLowerCase();
    return v.key.toLowerCase().includes(q) || v.description?.toLowerCase().includes(q);
  });

  const recommendedVars = filteredVariables.filter((v) => v.recommended);
  const generalVars = filteredVariables.filter((v) => !v.recommended);

  // Effective preview compiled HTML
  const compiledForPreview = generateEmailHtml({
    ...builderConfig,
    blocks,
  });
  const effectivePreviewHtml = renderWithVariables(
    activeBodyTab === "code"
      ? rawHtml
      : activeBodyTab === "classic"
      ? classicContent
      : compiledForPreview,
    testVariables
  );
  const effectivePreviewSubject = renderWithVariables(subject || "Email Notification", testVariables);

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  return (
    <Layout>
      <div className="w-full max-w-full space-y-4 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & ACTIONS BANNER                           */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Email Templates"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-0.5 min-w-0">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
                {isEdit ? "Edit Email Template" : "Add Email Template"}
              </h1>
              <p className="text-xs text-slate-800 font-bold">
                Here you can see all about your Email Template
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-4 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-5 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98] disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Save Changes" : "Save Template"}</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. BASIC INFORMATION CARD                                */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-5 border border-slate-300 shadow-xs space-y-3">
          <h2 className="text-xs font-bold text-slate-800">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Type with Predefined Prompt */}
            <div>
              <FormControl
                type="select"
                label="Type"
                required={true}
                placeholder="Select Email Type"
                theme="search"
                options={typeOptions}
                value={emailType}
                onChange={handleTypeSelect}
              />
            </div>

            {/* Title */}
            <div>
              <FormControl
                type="text"
                label="Title"
                required={true}
                placeholder="e.g. Order Status Notification"
                value={name}
                onChange={setName}
              />
            </div>

            {/* Subject */}
            <div>
              <FormControl
                type="text"
                label="Subject"
                required={true}
                placeholder="e.g. Find Your Order Status"
                value={subject}
                onChange={setSubject}
              />
            </div>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 3. BODY CONTENT HEADER & TABS                            */}
        {/* ======================================================== */}
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-slate-300 pb-2">
            <h2 className="text-xs font-bold text-slate-800 flex items-center gap-1">
              <span>Body Content</span>
              <span className="text-rose-500 font-black">*</span>
            </h2>

            {/* The 4 Subtabs */}
            <div className="flex items-center gap-5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setActiveBodyTab("visual")}
                className={`pb-2 transition-colors cursor-pointer relative ${
                  activeBodyTab === "visual"
                    ? "text-[#115B9D] font-bold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <span>Visual Builder</span>
                {activeBodyTab === "visual" && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!classicContent && blocks && blocks.length > 0) {
                    const bodyBlocks = blocks.filter((b) => b.type === "text" || b.type === "heading");
                    const bodyHtml = bodyBlocks
                      .map((b) =>
                        b.type === "heading"
                          ? `<h${b.data?.level === "h1" ? "1" : "2"}>${b.data?.text || ""}</h${b.data?.level === "h1" ? "1" : "2"}>`
                          : b.data?.html || ""
                      )
                      .join("");
                    setClassicContent(bodyHtml);
                  }
                  setActiveBodyTab("classic");
                }}
                className={`pb-2 transition-colors cursor-pointer relative ${
                  activeBodyTab === "classic"
                    ? "text-[#115B9D] font-bold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <span>Classic Editor</span>
                {activeBodyTab === "classic" && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveBodyTab("code")}
                className={`pb-2 transition-colors cursor-pointer relative ${
                  activeBodyTab === "code"
                    ? "text-[#115B9D] font-bold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <span>Html Code</span>
                {activeBodyTab === "code" && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveBodyTab("preview")}
                className={`pb-2 transition-colors cursor-pointer relative ${
                  activeBodyTab === "preview"
                    ? "text-[#115B9D] font-bold"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <span>Live Preview</span>
                {activeBodyTab === "preview" && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                )}
              </button>
            </div>
          </div>

          {/* ======================================================== */}
          {/* TAB 1: VISUAL BUILDER (SPLIT CANVAS & STYLE SETTINGS)     */}
          {/* ======================================================== */}
          {activeBodyTab === "visual" && (
            <div className="bg-white rounded-2xl border border-slate-300 shadow-xs overflow-hidden flex flex-col">
              {/* Toolbar Header Bar */}
              <div className="flex items-center justify-between border-b border-slate-300 bg-white">
                {/* Left: DESIGN CANVAS, Undo, Redo, Device Toggle */}
                <div className="flex items-center justify-between flex-1 px-5 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-black tracking-wider text-slate-800 uppercase">
                      DESIGN CANVAS
                    </span>
                    <div className="flex items-center gap-1 border-l border-slate-300 pl-3">
                      <button
                        type="button"
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                        className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                          historyIndex > 0
                            ? "text-slate-700 hover:bg-slate-100"
                            : "text-slate-300 cursor-not-allowed"
                        }`}
                        title="Undo"
                      >
                        <FiRotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                        className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                          historyIndex < history.length - 1
                            ? "text-slate-700 hover:bg-slate-100"
                            : "text-slate-300 cursor-not-allowed"
                        }`}
                        title="Redo"
                      >
                        <FiRotateCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Device Toggle Buttons */}
                  <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-300">
                    <button
                      type="button"
                      onClick={() => setCanvasDevice("desktop")}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        canvasDevice === "desktop"
                          ? "bg-[#0F172A] text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <FiMonitor className="w-3 h-3" />
                      <span>Desktop</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCanvasDevice("mobile")}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        canvasDevice === "mobile"
                          ? "bg-[#0F172A] text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <FiSmartphone className="w-3 h-3" />
                      <span>Mobile</span>
                    </button>
                  </div>
                </div>

                {/* Right: Drawer Tabs Header */}
                <div className="w-80 md:w-96 shrink-0 border-l border-slate-300 flex items-center justify-around bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setRightTab("add")}
                    className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1 transition-colors cursor-pointer relative ${
                      rightTab === "add"
                        ? "text-[#115B9D] bg-white font-extrabold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    <span>Add</span>
                    {rightTab === "add" && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setRightTab("style")}
                    className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1 transition-colors cursor-pointer relative ${
                      rightTab === "style"
                        ? "text-[#115B9D] bg-white font-extrabold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <FiSliders className="w-3.5 h-3.5" />
                    <span>Style</span>
                    {rightTab === "style" && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setRightTab("insert")}
                    className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1 transition-colors cursor-pointer relative ${
                      rightTab === "insert"
                        ? "text-[#115B9D] bg-white font-extrabold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <FiTag className="w-3.5 h-3.5" />
                    <span>Insert</span>
                    {rightTab === "insert" && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#115B9D]" />
                    )}
                  </button>
                </div>
              </div>

              {/* Main Visual Builder Body */}
              <div className="flex flex-col lg:flex-row min-h-[580px]">
                {/* ---------------------------------------------------- */}
                {/* LEFT CANVAS AREA                                     */}
                {/* ---------------------------------------------------- */}
                <div className="flex-1 bg-[#F8FAFC] p-6 sm:p-8 flex justify-center items-start overflow-y-auto">
                  <div
                    className={`transition-all duration-300 w-full ${
                      canvasDevice === "mobile" ? "max-w-[375px]" : "max-w-[560px]"
                    }`}
                  >
                    {blocks.length === 0 ? (
                      /* Clean Empty Dropzone State */
                      <div className="border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center bg-white shadow-xs">
                        <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-[#115B9D] flex items-center justify-center mb-3">
                          <FiLayers className="w-7 h-7" />
                        </div>
                        <h3 className="font-bold text-slate-800 text-sm sm:text-base">
                          Drag a component here to start building your email
                        </h3>
                        <p className="text-xs text-slate-500 mt-1.5 max-w-sm mx-auto">
                          Click any element in the right library panel to insert it into your design canvas, or select an Email Type above to load a predefined template.
                        </p>
                      </div>
                    ) : (
                      /* Canvas Master Card */
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                        }}
                        className="shadow-sm border border-slate-300 overflow-hidden space-y-0"
                      >
                        {blocks.map((block, index) => {
                          const isSelected = selectedBlockId === block.id;
                          const data = block.data || {};

                          return (
                            <div
                              key={block.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectBlock(block.id);
                              }}
                              className={`relative transition-all cursor-pointer ${
                                isSelected
                                  ? "border-2 border-[#115B9D] z-20"
                                  : "hover:border-2 hover:border-[#115B9D]/60 border-2 border-transparent"
                              }`}
                            >
                              {/* 1. Header Block */}
                              {block.type === "header" && (
                                <div
                                  style={{
                                    backgroundColor: data.bg_color || "#115B9D",
                                    paddingTop: `${data.padding_top || 20}px`,
                                    paddingBottom: `${data.padding_bottom || 20}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    fontFamily: data.fontFamily || "Arial",
                                  }}
                                  className={`flex text-white transition-all ${
                                    data.layout === "centered"
                                      ? "flex-col items-center justify-center gap-2 text-center"
                                      : "flex-row items-center justify-between gap-4"
                                  }`}
                                >
                                  {/* Logo placeholder */}
                                  {data.logo_show !== false && (
                                    <div className="inline-flex items-center px-3 py-1 rounded border border-white/40 text-xs font-mono font-bold shrink-0">
                                      {data.logo_var || "{companyLogo}"}
                                    </div>
                                  )}

                                  {/* Company Name & Subtitle */}
                                  <div className={data.layout === "centered" ? "text-center space-y-1" : "text-right space-y-0.5"}>
                                    {data.name_show !== false && (
                                      <div
                                        style={{
                                          fontSize: data.name_font_size || "20px",
                                          fontWeight: data.isBold !== false ? 700 : 400,
                                          fontStyle: data.isItalic ? "italic" : "normal",
                                          color: data.name_color || "#ffffff",
                                        }}
                                      >
                                        {data.name_var || "{companyName}"}
                                      </div>
                                    )}
                                    {data.subtitle && (
                                      <div
                                        style={{ color: data.subtitle_color || "#bfdbfe" }}
                                        className="text-[11px] font-semibold uppercase tracking-wider"
                                      >
                                        {data.subtitle}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* 2. Heading Block */}
                              {block.type === "heading" && (
                                <div
                                  style={{
                                    paddingTop: `${data.padding_top || 20}px`,
                                    paddingBottom: `${data.padding_bottom || 12}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    textAlign: data.align || "center",
                                    fontFamily: data.fontFamily || "Arial",
                                  }}
                                >
                                  <h2
                                    style={{
                                      margin: 0,
                                      color: data.color || "#0f172a",
                                      fontSize: data.fontSize || "22px",
                                      fontWeight: data.isBold !== false ? 700 : 400,
                                      fontStyle: data.isItalic ? "italic" : "normal",
                                    }}
                                  >
                                    {data.text || "Estimate for {estimateTitle}"}
                                  </h2>
                                </div>
                              )}

                              {/* 3. Text Block */}
                              {block.type === "text" && (
                                <div
                                  style={{
                                    paddingTop: `${data.padding_top || 14}px`,
                                    paddingBottom: `${data.padding_bottom || 14}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    textAlign: data.align || "left",
                                    color: data.color || "#334155",
                                    fontSize: data.fontSize || "14px",
                                    fontFamily: data.fontFamily || "Arial",
                                    fontWeight: data.isBold ? 700 : 400,
                                    fontStyle: data.isItalic ? "italic" : "normal",
                                    lineHeight: 1.6,
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      data.html ||
                                      "<p>Hi {clientName},<br/>Please find attached the estimate for the works we discussed.</p>",
                                  }}
                                />
                              )}

                              {/* 4. Action Button Block */}
                              {block.type === "button" && (
                                <div
                                  style={{
                                    paddingTop: `${data.padding_top || 16}px`,
                                    paddingBottom: `${data.padding_bottom || 16}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    textAlign: data.align || "left",
                                    fontFamily: data.fontFamily || "Arial",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-block",
                                      backgroundColor: data.bg_color || "#059669",
                                      color: data.text_color || "#ffffff",
                                      borderRadius: `${data.border_radius || 8}px`,
                                      padding: "10px 24px",
                                      fontSize: data.fontSize || "14px",
                                      fontWeight: data.isBold !== false ? 700 : 400,
                                    }}
                                    className="shadow-xs"
                                  >
                                    {data.label || "Accept Estimate"}
                                  </span>
                                </div>
                              )}

                              {/* 5. Image Block */}
                              {block.type === "image" && (
                                <div
                                  style={{
                                    paddingTop: `${data.padding_top || 16}px`,
                                    paddingBottom: `${data.padding_bottom || 16}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    textAlign: data.align || "center",
                                  }}
                                >
                                  <div className="inline-flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-mono font-bold text-slate-800">
                                    <FiImage className="w-4 h-4 text-slate-600" />
                                    <span>{data.url || "{companyLogo}"}</span>
                                  </div>
                                </div>
                              )}

                              {/* 6. Divider Block */}
                              {block.type === "divider" && (
                                <div
                                  style={{
                                    paddingTop: `${data.padding_top || 12}px`,
                                    paddingBottom: `${data.padding_bottom || 12}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                  }}
                                >
                                  <hr
                                    style={{
                                      border: "none",
                                      borderTop: `${data.thickness || 1}px solid ${
                                        data.color || "#e2e8f0"
                                      }`,
                                      margin: 0,
                                    }}
                                  />
                                </div>
                              )}

                              {/* 7. Footer Block */}
                              {block.type === "footer" && (
                                <div
                                  style={{
                                    backgroundColor: data.bg_color || "#0f172a",
                                    paddingTop: `${data.padding_top || 20}px`,
                                    paddingBottom: `${data.padding_bottom || 20}px`,
                                    paddingLeft: `${data.padding_left || 24}px`,
                                    paddingRight: `${data.padding_right || 24}px`,
                                    textAlign: data.align || "center",
                                    fontSize: data.fontSize || "12px",
                                    color: data.text_color || "#cbd5e1",
                                    fontFamily: data.fontFamily || "Arial",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  <p className="m-0 font-medium">
                                    {data.copyright_text ||
                                      "{companyName}. All rights reserved."}
                                  </p>
                                  {data.disclaimer && (
                                    <p className="m-0 mt-1 text-[11px] opacity-80">
                                      {data.disclaimer}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* ---------------------------------------------------- */}
                {/* RIGHT DRAWER: ADD / STYLE / INSERT TABS              */}
                {/* ---------------------------------------------------- */}
                <div className="w-full lg:w-80 md:w-96 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-300 bg-white flex flex-col">
                  {/* TAB A: ADD COMPONENTS */}
                  {rightTab === "add" && (
                    <div className="p-4 space-y-3">
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                        DRAG & CLICK TO ADD
                      </div>

                      <div className="space-y-2">
                        {/* 1. Branding Header */}
                        <button
                          type="button"
                          onClick={() => addBlock("header")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold text-xs shrink-0">
                            H
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Branding Header
                            </div>
                            <div className="text-[10px] text-slate-500">Logo, title & banner</div>
                          </div>
                        </button>

                        {/* 2. Large Title Text */}
                        <button
                          type="button"
                          onClick={() => addBlock("heading")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center font-bold text-xs shrink-0">
                            A
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Large Title Text
                            </div>
                            <div className="text-[10px] text-slate-500">Section headline</div>
                          </div>
                        </button>

                        {/* 3. Standard Body Text */}
                        <button
                          type="button"
                          onClick={() => addBlock("text")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center font-bold text-sm shrink-0">
                            ¶
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Standard Body Text
                            </div>
                            <div className="text-[10px] text-slate-500">Paragraph with merge tags</div>
                          </div>
                        </button>

                        {/* 4. Action Button */}
                        <button
                          type="button"
                          onClick={() => addBlock("button")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center text-xs shrink-0">
                            ■
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Action Button
                            </div>
                            <div className="text-[10px] text-slate-500">Call to action button</div>
                          </div>
                        </button>

                        {/* 5. Image / Logo */}
                        <button
                          type="button"
                          onClick={() => addBlock("image")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center text-sm shrink-0">
                            <FiImage className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Image / Logo
                            </div>
                            <div className="text-[10px] text-slate-500">Branded image asset</div>
                          </div>
                        </button>

                        {/* 6. Divider Line */}
                        <button
                          type="button"
                          onClick={() => addBlock("divider")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center text-xs shrink-0">
                            —
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Divider Line
                            </div>
                            <div className="text-[10px] text-slate-500">Separation rule</div>
                          </div>
                        </button>

                        {/* 7. Footer & Support */}
                        <button
                          type="button"
                          onClick={() => addBlock("footer")}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all text-left cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-300 flex items-center justify-center text-xs shrink-0">
                            📄
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 group-hover:text-[#115B9D]">
                              Footer & Support
                            </div>
                            <div className="text-[10px] text-slate-500">Contact & copyright</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* TAB B: STYLE SETTINGS */}
                  {rightTab === "style" && (
                    <div className="p-4 space-y-4 max-h-[640px] overflow-y-auto">
                      {!selectedBlock ? (
                        <div className="py-12 text-center text-xs text-slate-500 space-y-2">
                          <FiSliders className="w-6 h-6 mx-auto text-slate-400" />
                          <p>Click any block on the canvas to configure its settings.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 border-b border-slate-200 pb-2.5">
                            <FiEdit3 className="w-4 h-4 text-slate-700" />
                            <h3 className="text-xs font-bold text-slate-900">
                              {selectedBlock.type === "header" && "Header Settings"}
                              {selectedBlock.type === "heading" && "Heading Settings"}
                              {selectedBlock.type === "text" && "Paragraph Settings"}
                              {selectedBlock.type === "button" && "Button Settings"}
                              {selectedBlock.type === "image" && "Image Settings"}
                              {selectedBlock.type === "divider" && "Divider Settings"}
                              {selectedBlock.type === "footer" && "Footer Settings"}
                            </h3>
                          </div>

                          {/* TEXT FONT & SIZE GROUP */}
                          <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide block">
                              TEXT FONT & SIZE
                            </span>

                            <div className="grid grid-cols-2 gap-2">
                              {/* Font */}
                              <div>
                                <label className="text-[10px] text-slate-500 block mb-1">Font</label>
                                <select
                                  value={selectedBlock.data.fontFamily || "Arial"}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "fontFamily", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 font-medium outline-none"
                                >
                                  {EMAIL_FONTS.map((f) => (
                                    <option key={f} value={f}>
                                      {f}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Size */}
                              <div>
                                <label className="text-[10px] text-slate-500 block mb-1">Size</label>
                                <select
                                  value={
                                    selectedBlock.data.fontSize ||
                                    selectedBlock.data.name_font_size ||
                                    "16px"
                                  }
                                  onChange={(e) => {
                                    if (selectedBlock.type === "header") {
                                      updateBlockData(selectedBlock.id, "name_font_size", e.target.value);
                                    } else {
                                      updateBlockData(selectedBlock.id, "fontSize", e.target.value);
                                    }
                                  }}
                                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 font-medium outline-none"
                                >
                                  {FONT_SIZES.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Format Buttons Row: [B] [I] Colour: [ ] BG: [ ] */}
                            <div className="flex items-center gap-3 pt-1">
                              <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateBlockData(
                                      selectedBlock.id,
                                      "isBold",
                                      !selectedBlock.data.isBold
                                    )
                                  }
                                  className={`px-2.5 py-1 text-xs font-bold transition-colors cursor-pointer ${
                                    selectedBlock.data.isBold
                                      ? "bg-[#115B9D] text-white"
                                      : "text-slate-700 hover:bg-slate-100"
                                  }`}
                                  title="Bold"
                                >
                                  B
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateBlockData(
                                      selectedBlock.id,
                                      "isItalic",
                                      !selectedBlock.data.isItalic
                                    )
                                  }
                                  className={`px-2.5 py-1 text-xs italic transition-colors cursor-pointer border-l border-slate-200 ${
                                    selectedBlock.data.isItalic
                                      ? "bg-[#115B9D] text-white"
                                      : "text-slate-700 hover:bg-slate-100"
                                  }`}
                                  title="Italic"
                                >
                                  I
                                </button>
                              </div>

                              {/* Text Colour */}
                              <div className="flex items-center gap-1.5 text-xs text-slate-700 font-medium">
                                <span className="text-[11px]">Colour:</span>
                                <input
                                  type="color"
                                  value={
                                    selectedBlock.data.color ||
                                    selectedBlock.data.name_color ||
                                    selectedBlock.data.text_color ||
                                    "#0F172A"
                                  }
                                  onChange={(e) => {
                                    if (selectedBlock.type === "header") {
                                      updateBlockData(selectedBlock.id, "name_color", e.target.value);
                                    } else if (selectedBlock.type === "button" || selectedBlock.type === "footer") {
                                      updateBlockData(selectedBlock.id, "text_color", e.target.value);
                                    } else {
                                      updateBlockData(selectedBlock.id, "color", e.target.value);
                                    }
                                  }}
                                  className="w-6 h-6 rounded border border-slate-300 p-0 cursor-pointer"
                                />
                              </div>

                              {/* Background Colour */}
                              <div className="flex items-center gap-1.5 text-xs text-slate-700 font-medium">
                                <span className="text-[11px]">BG:</span>
                                <input
                                  type="color"
                                  value={selectedBlock.data.bg_color || "#115B9D"}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "bg_color", e.target.value)
                                  }
                                  className="w-6 h-6 rounded border border-slate-300 p-0 cursor-pointer"
                                />
                              </div>
                            </div>
                          </div>

                          {/* COMPONENT-SPECIFIC SETTINGS */}
                          {/* 1. Header */}
                          {selectedBlock.type === "header" && (
                            <div className="space-y-3">
                              <div className="space-y-1.5">
                                <span className="text-xs font-semibold text-slate-800">
                                  Header Layout
                                </span>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "layout", "side-by-side")
                                    }
                                    className={`py-1.5 px-3 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                      selectedBlock.data.layout !== "centered"
                                        ? "bg-white border-[#115B9D] text-[#115B9D] shadow-2xs font-bold"
                                        : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                                    }`}
                                  >
                                    ☷ Side by Side
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "layout", "centered")
                                    }
                                    className={`py-1.5 px-3 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                      selectedBlock.data.layout === "centered"
                                        ? "bg-white border-[#115B9D] text-[#115B9D] shadow-2xs font-bold"
                                        : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                                    }`}
                                  >
                                    ☵ Centered
                                  </button>
                                </div>
                              </div>

                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-semibold text-slate-800">
                                    Company Name
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "name_var", "{{platform_name}}")
                                    }
                                    className="text-[11px] font-bold text-[#115B9D] hover:underline cursor-pointer"
                                  >
                                    + Insert {"{{platform_name}}"}
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={selectedBlock.data.name_var || "{{platform_name}}"}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "name_var", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                                />
                              </div>

                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-semibold text-slate-800">
                                    Company Logo
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "logo_var", "{{platform_logo}}")
                                    }
                                    className="text-[11px] font-bold text-[#115B9D] hover:underline cursor-pointer"
                                  >
                                    + Insert {"{{platform_logo}}"}
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={selectedBlock.data.logo_var || "{{platform_logo}}"}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "logo_var", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                                />
                                <span className="text-[10px] text-slate-500 block">
                                  Enter direct logo image URL or click button above to insert dynamic logo tag.
                                </span>
                              </div>
                            </div>
                          )}

                          {/* 2. Heading */}
                          {selectedBlock.type === "heading" && (
                            <div className="space-y-3">
                              <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-800">
                                  Title Text
                                </label>
                                <input
                                  type="text"
                                  value={selectedBlock.data.text || ""}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "text", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                                />
                              </div>
                            </div>
                          )}

                          {/* 3. Text */}
                          {selectedBlock.type === "text" && (
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-slate-800">
                                Edit Text Content
                              </label>

                              <div className="flex items-center gap-1 border border-slate-300 rounded-t-lg bg-slate-50 p-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateBlockData(
                                      selectedBlock.id,
                                      "html",
                                      `<strong>${selectedBlock.data.html || ""}</strong>`
                                    )
                                  }
                                  className="p-1 rounded text-slate-700 hover:bg-slate-200 text-xs font-bold"
                                  title="Bold"
                                >
                                  B
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateBlockData(
                                      selectedBlock.id,
                                      "html",
                                      `<em>${selectedBlock.data.html || ""}</em>`
                                    )
                                  }
                                  className="p-1 rounded text-slate-700 hover:bg-slate-200 text-xs italic"
                                  title="Italic"
                                >
                                  I
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateBlockData(
                                      selectedBlock.id,
                                      "html",
                                      `<u>${selectedBlock.data.html || ""}</u>`
                                    )
                                  }
                                  className="p-1 rounded text-slate-700 hover:bg-slate-200 text-xs underline"
                                  title="Underline"
                                >
                                  U
                                </button>
                              </div>

                              <textarea
                                rows={7}
                                value={selectedBlock.data.html || selectedBlock.data.text || ""}
                                onChange={(e) =>
                                  updateBlockData(selectedBlock.id, "html", e.target.value)
                                }
                                className="w-full bg-white border border-t-0 border-slate-300 rounded-b-lg p-3 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D] leading-relaxed"
                              />
                            </div>
                          )}

                          {/* 4. Button */}
                          {selectedBlock.type === "button" && (
                            <div className="space-y-3">
                              <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-800">
                                  Button Label
                                </label>
                                <input
                                  type="text"
                                  value={selectedBlock.data.label || ""}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "label", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                                />
                              </div>

                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-semibold text-slate-800">
                                    Target URL
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "url", "{{action_url}}")
                                    }
                                    className="text-[11px] font-bold text-[#115B9D] hover:underline cursor-pointer"
                                  >
                                    + Insert {"{{action_url}}"}
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={selectedBlock.data.url || ""}
                                  onChange={(e) =>
                                    updateBlockData(selectedBlock.id, "url", e.target.value)
                                  }
                                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                                />
                              </div>
                            </div>
                          )}

                          {/* 5. Footer */}
                          {selectedBlock.type === "footer" && (
                            <div className="space-y-2">
                              <label className="text-xs font-semibold text-slate-800">
                                Footer Content
                              </label>
                              <textarea
                                rows={5}
                                value={
                                  selectedBlock.data.disclaimer ||
                                  selectedBlock.data.copyright_text ||
                                  ""
                                }
                                onChange={(e) => {
                                  updateBlockData(selectedBlock.id, "disclaimer", e.target.value);
                                  updateBlockData(selectedBlock.id, "copyright_text", e.target.value);
                                }}
                                className="w-full bg-white border border-slate-300 rounded-lg p-3 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D] leading-relaxed"
                              />
                            </div>
                          )}

                          {/* ADVANCED STYLING: TEXT ALIGNMENT & PADDING */}
                          <div className="space-y-3 pt-2 border-t border-slate-200">
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide block">
                              Advanced Styling
                            </span>

                            {/* Text Alignment */}
                            <div className="space-y-1">
                              <label className="text-[11px] text-slate-600 block">Text Alignment</label>
                              <div className="grid grid-cols-3 gap-1">
                                {["left", "center", "right"].map((alignOpt) => (
                                  <button
                                    key={alignOpt}
                                    type="button"
                                    onClick={() =>
                                      updateBlockData(selectedBlock.id, "align", alignOpt)
                                    }
                                    className={`py-1.5 rounded-lg text-xs font-semibold capitalize border transition-all cursor-pointer ${
                                      (selectedBlock.data.align || "left") === alignOpt
                                        ? "bg-white border-[#115B9D] text-[#115B9D] shadow-2xs font-bold"
                                        : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                                    }`}
                                  >
                                    {alignOpt}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Padding Spacing (Px) */}
                            <div className="space-y-1">
                              <label className="text-[11px] text-slate-600 block">
                                Padding Spacing (Px)
                              </label>
                              <div className="grid grid-cols-4 gap-1.5 text-center">
                                {["top", "bottom", "left", "right"].map((side) => {
                                  const field = `padding_${side}`;
                                  return (
                                    <div key={side}>
                                      <span className="text-[9px] text-slate-400 capitalize block mb-0.5">
                                        {side}
                                      </span>
                                      <input
                                        type="number"
                                        value={selectedBlock.data[field] ?? 16}
                                        onChange={(e) =>
                                          updateBlockData(
                                            selectedBlock.id,
                                            field,
                                            parseInt(e.target.value) || 0
                                          )
                                        }
                                        className="w-full bg-white border border-slate-300 rounded-lg px-1.5 py-1 text-xs text-center text-slate-900 font-bold outline-none"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* DUPLICATE & DELETE BUTTONS */}
                          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-200">
                            <button
                              type="button"
                              onClick={() => duplicateBlock(selectedBlock.id)}
                              className="w-full inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                            >
                              <FiCopy className="w-3.5 h-3.5 text-slate-600" />
                              <span>Duplicate Block</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => removeBlock(selectedBlock.id)}
                              className="w-full inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold transition-all cursor-pointer"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB C: INSERT MERGE TAGS (ORGANIZED BASED ON EMAIL TYPE!) */}
                  {rightTab === "insert" && (
                    <div className="p-4 space-y-4 max-h-[640px] overflow-y-auto">
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] font-black text-slate-800 uppercase tracking-wider">
                          MERGE TAGS {emailType ? `(${emailType.replace(/_/g, " ")})` : ""}
                        </div>
                        {emailType && (
                          <span className="text-[10px] font-bold text-[#115B9D] bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                            Type Filtered
                          </span>
                        )}
                      </div>

                      <input
                        type="text"
                        value={varSearchQuery}
                        onChange={(e) => setVarSearchQuery(e.target.value)}
                        placeholder="Search tags..."
                        className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs text-slate-900 font-medium outline-none focus:border-[#115B9D]"
                      />

                      {/* 1. RECOMMENDED TAGS FOR THIS EMAIL TYPE */}
                      {recommendedVars.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase text-amber-600 tracking-wide">
                            <FiStar className="w-3 h-3 fill-amber-500 text-amber-500" />
                            <span>Recommended for {emailType ? EMAIL_TYPE_MAP[emailType]?.name || emailType : "Template"}</span>
                          </div>

                          <div className="space-y-1.5">
                            {recommendedVars.map((v, idx) => (
                              <div
                                key={`rec-${idx}`}
                                onClick={() => handleInsertVariable(v.key)}
                                className="p-2.5 rounded-xl border border-amber-200 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition-all cursor-pointer space-y-0.5 group shadow-2xs"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-xs font-black text-amber-800 bg-white px-2 py-0.5 rounded border border-amber-300">
                                    {`{{${v.key}}}`}
                                  </span>
                                  <span className="text-[10px] font-bold text-amber-700 group-hover:underline">
                                    + Insert
                                  </span>
                                </div>
                                <div className="text-[11px] text-slate-700 font-semibold">
                                  {v.description}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 2. GENERAL COMPANY & CLIENT TAGS */}
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          General Platform & Client Tags
                        </div>

                        <div className="space-y-1.5">
                          {generalVars.map((v, idx) => (
                            <div
                              key={`gen-${idx}`}
                              onClick={() => handleInsertVariable(v.key)}
                              className="p-2 rounded-xl border border-slate-300 bg-white hover:border-[#115B9D] hover:shadow-xs transition-all cursor-pointer space-y-0.5 group"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-xs font-bold text-[#115B9D] bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                  {`{{${v.key}}}`}
                                </span>
                                <span className="text-[10px] font-semibold text-slate-500 group-hover:text-[#115B9D]">
                                  + Insert
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-700 font-medium">
                                {v.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 2: CLASSIC EDITOR                                    */}
          {/* ======================================================== */}
          {activeBodyTab === "classic" && (
            <div className="bg-white rounded-2xl border border-slate-300 shadow-xs p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <div>
                  <span className="text-xs font-bold text-slate-800 block">
                    WYSIWYG Rich Text Editor
                  </span>
                  <span className="text-xs text-slate-500">
                    Full email template design with platform header, editable body, and footer.
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  You can use dynamic merge tags like {"{{customer_name}}"} inside text.
                </span>
              </div>

              {/* Master Email Template Card */}
              <div className="bg-[#f1f5f9] p-4 sm:p-8 rounded-2xl border border-slate-300 flex justify-center">
                <div className="w-full max-w-[620px] bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden flex flex-col">
                  {/* 1. Header Banner (#115B9D) */}
                  <div
                    style={{
                      backgroundColor:
                        blocks.find((b) => b.type === "header")?.data?.bg_color || "#115B9D",
                    }}
                    className={`p-6 text-white transition-all ${
                      blocks.find((b) => b.type === "header")?.data?.layout === "centered"
                        ? "text-center flex flex-col items-center justify-center gap-2"
                        : "flex items-center justify-between gap-4"
                    }`}
                  >
                    <div className="inline-flex items-center px-3 py-1 rounded border border-white/40 text-xs font-mono font-bold shrink-0">
                      {blocks.find((b) => b.type === "header")?.data?.logo_var || "{{platform_logo}}"}
                    </div>
                    <div
                      className={
                        blocks.find((b) => b.type === "header")?.data?.layout === "centered"
                          ? "text-center space-y-1"
                          : "text-right space-y-0.5"
                      }
                    >
                      <div className="text-xl font-black text-white tracking-tight">
                        {blocks.find((b) => b.type === "header")?.data?.name_var || "{{platform_name}}"}
                      </div>
                      <div
                        style={{
                          color:
                            blocks.find((b) => b.type === "header")?.data?.subtitle_color || "#bfdbfe",
                        }}
                        className="text-[11px] font-semibold uppercase tracking-wider"
                      >
                        {blocks.find((b) => b.type === "header")?.data?.subtitle ||
                          "Official System Notification"}
                      </div>
                    </div>
                  </div>

                  {/* 2. WYSIWYG Editable Rich Text Body */}
                  <div className="p-6 sm:p-7 space-y-4 bg-white flex-1">
                    <ReactQuill
                      theme="snow"
                      value={classicContent}
                      onChange={(content) => {
                        setClassicContent(content);
                      }}
                      className="bg-white rounded-xl email-quill-editor"
                    />

                    {/* 3. Action Button Preview */}
                    {blocks.find((b) => b.type === "button") && (
                      <div
                        className={`pt-2 flex ${
                          blocks.find((b) => b.type === "button")?.data?.align === "center"
                            ? "justify-center"
                            : blocks.find((b) => b.type === "button")?.data?.align === "right"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div
                          style={{
                            backgroundColor:
                              blocks.find((b) => b.type === "button")?.data?.bg_color || "#115B9D",
                            color:
                              blocks.find((b) => b.type === "button")?.data?.text_color || "#ffffff",
                            borderRadius: `${
                              blocks.find((b) => b.type === "button")?.data?.border_radius || 8
                            }px`,
                          }}
                          className="px-6 py-3 font-bold text-sm shadow-md inline-block select-none"
                        >
                          {blocks.find((b) => b.type === "button")?.data?.label || "Action Link →"}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 4. Footer Banner (#115B9D) */}
                  <div
                    style={{
                      backgroundColor:
                        blocks.find((b) => b.type === "footer")?.data?.bg_color || "#115B9D",
                    }}
                    className="p-6 text-white text-center border-t border-white/15 space-y-2 transition-colors"
                  >
                    <p className="text-xs text-white font-semibold leading-relaxed m-0">
                      Support Contact:{" "}
                      <strong className="text-white">
                        {blocks.find((b) => b.type === "footer")?.data?.email_var || "{{platform_email}}"}
                      </strong>{" "}
                      &bull;{" "}
                      <span>
                        {blocks.find((b) => b.type === "footer")?.data?.phone_var || "{{platform_phone}}"}
                      </span>
                    </p>
                    <p className="text-[11px] text-blue-100/90 leading-relaxed m-0">
                      &copy; {"{{current_year}}"}{" "}
                      {blocks.find((b) => b.type === "footer")?.data?.copyright_text ||
                        "{{platform_name}}. All rights reserved."}
                    </p>
                    <p className="text-[10px] text-blue-200/75 leading-relaxed m-0">
                      {blocks.find((b) => b.type === "footer")?.data?.disclaimer ||
                        "This is an automated operational notification generated on behalf of your account."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 3: HTML CODE                                         */}
          {/* ======================================================== */}
          {activeBodyTab === "code" && (
            <div className="bg-white rounded-2xl border border-slate-300 shadow-xs p-5 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <div className="flex items-center gap-2">
                  <FiCode className="w-4 h-4 text-[#115B9D]" />
                  <span className="text-xs font-bold text-slate-900">Direct HTML Source Code</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(rawHtml);
                    toast.success("Copied HTML markup to clipboard.");
                  }}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-colors cursor-pointer border border-slate-300"
                >
                  Copy HTML
                </button>
              </div>

              <textarea
                rows={20}
                value={rawHtml}
                onChange={(e) => {
                  setRawHtml(e.target.value);
                  setIsHtmlModeDirty(true);
                }}
                placeholder="<!DOCTYPE html>..."
                className="w-full font-mono text-xs p-4 bg-slate-900 text-emerald-400 rounded-xl border border-slate-800 outline-none focus:ring-1 focus:ring-emerald-500 leading-relaxed"
              />
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 4: LIVE PREVIEW                                      */}
          {/* ======================================================== */}
          {activeBodyTab === "preview" && (
            <div className="bg-white rounded-2xl border border-slate-300 shadow-xs overflow-hidden">
              <div className="p-4 border-b border-slate-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    <h3 className="text-sm font-bold text-slate-900">
                      Live Rendered Email Preview
                    </h3>
                  </div>
                  <p className="text-xs text-slate-600">
                    Subject: <span className="font-semibold text-slate-900">{effectivePreviewSubject}</span>
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-lg bg-white p-0.5 border border-slate-300 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setPreviewTabDevice("desktop")}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        previewTabDevice === "desktop"
                          ? "bg-[#0F172A] text-white shadow-xs"
                          : "text-slate-700 hover:text-slate-950"
                      }`}
                    >
                      <FiMonitor className="w-3.5 h-3.5" />
                      <span>Desktop</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewTabDevice("mobile")}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                        previewTabDevice === "mobile"
                          ? "bg-[#0F172A] text-white shadow-xs"
                          : "text-slate-700 hover:text-slate-950"
                      }`}
                    >
                      <FiSmartphone className="w-3.5 h-3.5" />
                      <span>Mobile</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(effectivePreviewHtml);
                      toast.success("Copied compiled HTML with variables resolved.");
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-800 text-xs font-bold transition-colors cursor-pointer border border-slate-300"
                  >
                    <FiCopy className="w-3.5 h-3.5" />
                    <span>Copy HTML</span>
                  </button>
                </div>
              </div>

              <div className="p-6 sm:p-8 bg-[#F8FAFC] flex justify-center items-start min-h-[580px]">
                <div
                  className={`transition-all duration-300 bg-white rounded-xl shadow-md border border-slate-300 overflow-hidden ${
                    previewTabDevice === "mobile" ? "w-[375px] max-w-full" : "w-[600px] max-w-full"
                  }`}
                >
                  <div className="bg-slate-100 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between text-xs text-slate-700">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block" />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />
                    </div>
                    <div className="text-[11px] font-semibold text-slate-600">
                      {previewTabDevice === "mobile" ? "Mobile Client (375px)" : "Desktop Client (600px)"}
                    </div>
                  </div>

                  <iframe
                    title="Live Email Preview"
                    srcDoc={effectivePreviewHtml}
                    className="w-full h-[580px] border-0 bg-white block"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* 4. BOTTOM ACTION BUTTONS: PREVIEW & SAVE                 */}
          {/* ======================================================== */}
          <div className="flex items-center justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => setActiveBodyTab("preview")}
              className="px-5 py-2 rounded-xl bg-white hover:bg-slate-100 text-slate-800 text-xs font-bold border border-slate-300 transition-all cursor-pointer shadow-2xs"
            >
              Preview
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98] disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Save Changes" : "Save Template"}</span>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
